import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Tab,
    Backdrop,
    CircularProgress,
    Box,
    Pagination,
    Button,
    Typography,
    Collapse,
    Chip,
} from "@mui/material";
import OurLayouts from "../Layouts";
import apiService from "../../services/apiservice";
import { TiExport } from "react-icons/ti";
import { ProgressSpinner } from "primereact/progressspinner";
import { useGlobalContext } from "../../GlobalContext";
import { toast } from "react-toastify";
import moment from "moment";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { FaUserMd, FaClock } from "react-icons/fa";
import { MdAccessTime, MdLocationOn } from "react-icons/md";

const ITEMS_PER_PAGE = 10;

const RESPONSE_CODE_SUCCESS = "200";
const ROLE_CODE_CE = "CE";
const DATE_FORMAT = "DD-MM-YYYY";
const DATE_FORMAT_WITH_TIME = "DD-MM-YYYY HH:mm";
const EXPORT_FILE_NAME_FORMAT = "YYYY-MM-DD";

const TAB_INDICES = {
    ATTENDANCE: 0,
    REALTIME: 1,
    HOSPITAL_VISIT: 2,
};

const AgentReport = () => {
    const [loading, setLoading] = useState(false);
    const [realtimeLoading, setRealtimeLoading] = useState(false);
    const [hospitalVisitLoading, setHospitalVisitLoading] = useState(false);
    const { exporting, setExporting } = useGlobalContext();
    const [attendanceList, setAttendanceList] = useState([]);
    const [realtimeList, setRealtimeList] = useState([]);
    const [hospitalVisitList, setHospitalVisitList] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [realtimeCurrentPage, setRealtimeCurrentPage] = useState(1);
    const [hospitalVisitCurrentPage, setHospitalVisitCurrentPage] = useState(1);
    const [activeTab, setActiveTab] = useState(TAB_INDICES.ATTENDANCE);
    const [attendanceTotalRecords, setAttendanceTotalRecords] = useState(0);
    const [realtimeTotalRecords, setRealtimeTotalRecords] = useState(0);
    const [hospitalVisitTotalRecords, setHospitalVisitTotalRecords] = useState(0);
    const [conciergeUsers, setConciergeUsers] = useState([]);
    const [expandedRows, setExpandedRows] = useState({});

    // Refs for page numbers to ensure latest values are used in API calls
    const currentPageRef = useRef(1);
    const realtimeCurrentPageRef = useRef(1);
    const hospitalVisitCurrentPageRef = useRef(1);
    const activeTabRef = useRef(TAB_INDICES.ATTENDANCE);
    const filterObjRef = useRef({
        fromDate: moment().toDate(),
        toDate: moment().toDate(),
        userCode: null,
    });

    const [filterObj, setFilterObj] = useState({
        fromDate: moment().toDate(),
        toDate: moment().toDate(),
        userCode: null,
    });

    // Update refs when state changes
    useEffect(() => {
        currentPageRef.current = currentPage;
    }, [currentPage]);

    useEffect(() => {
        realtimeCurrentPageRef.current = realtimeCurrentPage;
    }, [realtimeCurrentPage]);

    useEffect(() => {
        hospitalVisitCurrentPageRef.current = hospitalVisitCurrentPage;
    }, [hospitalVisitCurrentPage]);

    useEffect(() => {
        activeTabRef.current = activeTab;
    }, [activeTab]);

    useEffect(() => {
        filterObjRef.current = filterObj;
    }, [filterObj]);

    const attendanceColumns = useMemo(() => [
        { field: "userCode", header: "User Code", width: "120px" },
        { field: "userName", header: "User Name", width: "150px" },
        { field: "loginDate", header: "Login Date", width: "120px" },
        { field: "firstLogin", header: "First Login", width: "140px" },
        { field: "lastLogout", header: "Last Logout", width: "140px" },
        { field: "totalHoursTime", header: "Total Hours", width: "120px" },
        { field: "noOfHospitalsVisited", header: "Hospitals Visited", width: "130px" },
        { field: "enquiryCount", header: "Enquiry Counts", width: "130px" },
    ], []);

    const realtimeColumns = useMemo(() => [
        { field: "ceName", header: "CE Name", width: "120px" },
        { field: "hospitalName", header: "Hospital Name", width: "150px" },
        { field: "enquiryId", header: "Enquiry ID", width: "100px" },
        { field: "enquiryDate", header: "Enquiry Date", width: "120px" },
        { field: "patientId", header: "Patient ID", width: "120px" },
        { field: "patientName", header: "Patient Name", width: "150px" },
        { field: "enquiryStatusName", header: "Enquiry Status", width: "150px" },
        { field: "conciergeStatusName", header: "Concierge Status", width: "150px" },
        { field: "assignmentType", header: "Assignment Type", width: "130px" },
    ], []);

    const hospitalVisitColumns = useMemo(() => [
        { field: "createdDate", header: "Date", width: "120px", icon: <MdAccessTime /> },
        { field: "userId", header: "User Code", width: "120px", icon: <FaUserMd /> },
        { field: "userName", header: "User Name", width: "150px", icon: <FaUserMd /> },
    ], []);

    const visitDetailColumns = useMemo(() => [
        { field: "hospitalCode", header: "Hospital Code", width: "120px", icon: <MdLocationOn /> },
        { field: "hospitalName", header: "Hospital Name", width: "200px", icon: <FaClock /> },
        { field: "checkIn", header: "Check In", width: "140px", icon: <FaClock /> },
        { field: "checkOut", header: "Check Out", width: "140px", icon: <FaClock /> },
        { field: "hoursSpent", header: "Hours Spent", width: "120px", icon: <MdAccessTime /> },
    ], []);

    const formatDate = useCallback((date, format) => {
        if (!date) return null;
        return moment(date).format(format || "MM/DD/YYYY");
    }, []);

    const cleanPayload = useCallback((payload) => {
        const cleaned = { ...payload };
        Object.keys(cleaned).forEach(key => {
            if (cleaned[key] === null || cleaned[key] === "" || cleaned[key] === undefined) {
                delete cleaned[key];
            }
        });
        return cleaned;
    }, []);

    const getConciergeUsers = useCallback(async () => {
        try {
            const payload = {
                roleCode: ROLE_CODE_CE,
                status: 1
            };
            const res = await apiService.getUserList(payload);

            if (res.data?.responseCode === RESPONSE_CODE_SUCCESS) {
                const users = res.data.successList || res.data.data || [];
                const formattedUsers = users.map(user => ({
                    value: user.code,
                    name: user.userName
                }));
                setConciergeUsers(formattedUsers);
            }
        } catch (error) {
            console.error("Error fetching concierge users:", error);
            toast.error("Failed to load user list");
        }
    }, []);

    const getAttendanceTracking = useCallback(async () => {
        setAttendanceList([]);
        setLoading(true);
        try {
            const currentFilter = filterObjRef.current;
            const payload = cleanPayload({
                pageNo: currentPageRef.current,
                pageSize: ITEMS_PER_PAGE,
                fromDate: currentFilter.fromDate ? formatDate(currentFilter.fromDate) : null,
                toDate: currentFilter.toDate ? formatDate(currentFilter.toDate) : null,
                userCode: currentFilter.userCode,
            });

            const res = await apiService.attendanceTracking(payload);

            if (res.data?.responseCode === RESPONSE_CODE_SUCCESS) {
                const data = res.data.successObject?.content ||
                    res.data.data?.content ||
                    res.data.content ||
                    [];

                const total = res.data.successObject?.totalElements ||
                    res.data.data?.totalElements ||
                    res.data.totalElements ||
                    0;

                setAttendanceList(data);
                setAttendanceTotalRecords(total);
            } else {
                setAttendanceList([]);
                setAttendanceTotalRecords(0);
            }
        } catch (error) {
            console.error("Error fetching attendance tracking:", error);
            toast.error("Failed to load attendance data");
            setAttendanceList([]);
            setAttendanceTotalRecords(0);
        } finally {
            setLoading(false);
        }
    }, [formatDate, cleanPayload]);

    const getRealtimeTracking = useCallback(async () => {
        setRealtimeList([]);
        setRealtimeLoading(true);
        try {
            const currentFilter = filterObjRef.current;
            const dateFormat = "YYYY-MM-DD";
            const payload = cleanPayload({
                pageNo: realtimeCurrentPageRef.current,
                pageSize: ITEMS_PER_PAGE,
                userCode: currentFilter.userCode,
                export: false,
                startDate: currentFilter.fromDate ? formatDate(currentFilter.fromDate, dateFormat) : null,
                endDate: currentFilter.toDate ? formatDate(currentFilter.toDate, dateFormat) : null,
            });

            const res = await apiService.realTimeTracking(payload);

            if (res.data?.responseCode === RESPONSE_CODE_SUCCESS) {
                const data = res.data.successObject?.content || [];
                const total = res.data.successObject?.totalElements || 0;

                setRealtimeList(data);
                setRealtimeTotalRecords(total);
            } else {
                toast.error("No data found");
                setRealtimeList([]);
                setRealtimeTotalRecords(0);
            }
        } catch (error) {
            console.error("Error fetching realtime tracking:", error);
            toast.error("Failed to load realtime data");
            setRealtimeList([]);
            setRealtimeTotalRecords(0);
        } finally {
            setRealtimeLoading(false);
        }
    }, [formatDate, cleanPayload]);

    const calculateHoursFromTimeString = useCallback((timeString) => {
        if (!timeString) return 0;
        try {
            const [hours, minutes, seconds] = timeString.split(':').map(Number);
            return hours + (minutes / 60) + (seconds / 3600);
        } catch {
            return 0;
        }
    }, []);

    const getHospitalVisitTracking = useCallback(async () => {
        setHospitalVisitList([]);
        setHospitalVisitLoading(true);
        try {
            const currentFilter = filterObjRef.current;
            const payload = cleanPayload({
                pageNo: hospitalVisitCurrentPageRef.current,
                pageSize: ITEMS_PER_PAGE,
                fromDate: currentFilter.fromDate ? formatDate(currentFilter.fromDate) : null,
                toDate: currentFilter.toDate ? formatDate(currentFilter.toDate) : null,
                userCode: currentFilter.userCode,
            });

            const res = await apiService.hospitalVisitTracking(payload);

            if (res.data?.content) {
                const data = res.data.content || [];
                const total = res.data.totalElements || 0;

                const formattedData = data.map((item, index) => {
                    const visits = item.attendanceList || [];
                    const totalVisits = visits.length;

                    const totalHours = visits.reduce((sum, visit) => {
                        return sum + calculateHoursFromTimeString(visit.hoursSpent);
                    }, 0);

                    const totalHoursFormatted = totalHours ? totalHours.toFixed(2) : "0.00";

                    return {
                        ...item,
                        attendanceList: visits,
                        totalVisits,
                        totalHours: totalHoursFormatted,
                        totalHoursFormatted: `${totalHoursFormatted} hrs`,
                        key: `${item.userId}_${item.createdDate}_${index}`
                    };
                });

                const defaultExpandedRows = {};
                formattedData.forEach(item => {
                    if (item.attendanceList && item.attendanceList.length > 0) {
                        defaultExpandedRows[item.key] = true;
                    }
                });

                setHospitalVisitList(formattedData);
                setHospitalVisitTotalRecords(total);
                setExpandedRows(defaultExpandedRows);
            } else {
                setHospitalVisitList([]);
                setHospitalVisitTotalRecords(0);
                setExpandedRows({});
            }
        } catch (error) {
            console.error("Error fetching hospital visit tracking:", error);
            toast.error("Failed to load hospital visit data");
            setHospitalVisitList([]);
            setHospitalVisitTotalRecords(0);
            setExpandedRows({});
        } finally {
            setHospitalVisitLoading(false);
        }
    }, [formatDate, calculateHoursFromTimeString, cleanPayload]);

    const buildPayload = (filter, tab) => {
        const isRealtime = tab === TAB_INDICES.REALTIME;
        const dateFormat = isRealtime ? "YYYY-MM-DD" : null;

        const basePayload = {
            userCode: filter.userCode,
        };

        const datePayload = isRealtime
            ? {
                startDate: filter.fromDate
                    ? formatDate(filter.fromDate, dateFormat)
                    : null,
                endDate: filter.toDate
                    ? formatDate(filter.toDate, dateFormat)
                    : null,
            }
            : {
                fromDate: filter.fromDate
                    ? formatDate(filter.fromDate, dateFormat)
                    : null,
                toDate: filter.toDate
                    ? formatDate(filter.toDate, dateFormat)
                    : null,
            };

        return cleanPayload({ ...basePayload, ...datePayload });
    };

    const downloadExcel = (data, tab) => {
        const today = moment().format(EXPORT_FILE_NAME_FORMAT);
        const tabNames = ["attendance", "realtime", "hospital_visit"];
        const fileName = `agent_${tabNames[tab]}_${today}.xlsx`;

        const blob = new Blob([data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = globalThis.URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        globalThis.URL.revokeObjectURL(url);
    };

    const exportToExcel = useCallback(async () => {
        setExporting(true);

        try {
            const currentFilter = filterObjRef.current;
            const currentActiveTab = activeTabRef.current;

            const payload = buildPayload(currentFilter, currentActiveTab);

            const serviceMap = {
                [TAB_INDICES.ATTENDANCE]: () =>
                    apiService.attendanceTracking({ ...payload, export: true }),

                [TAB_INDICES.REALTIME]: () =>
                    apiService.realTimeTracking({ ...payload, export: true }),

                [TAB_INDICES.HOSPITAL_VISIT]: () =>
                    apiService.hospitalVisitTrackingXL(payload),
            };

            const res = await serviceMap[currentActiveTab]?.();

            if (res?.data) {
                downloadExcel(res.data, currentActiveTab);
                toast.success("Export completed successfully");
            }
        } catch (error) {
            console.error("Error exporting data:", error);
            toast.error("Failed to export data");
        } finally {
            setExporting(false);
        }
    }, [formatDate, cleanPayload]);


    const handleDateChange = useCallback((e, field) => {
        setFilterObj(prev => ({
            ...prev,
            [field]: e.value
        }));
    }, []);

    const handleSearch = useCallback(() => {
        setAttendanceList([]);
        setRealtimeList([]);
        setHospitalVisitList([]);

        setCurrentPage(1);
        setRealtimeCurrentPage(1);
        setHospitalVisitCurrentPage(1);

        const currentActiveTab = activeTabRef.current;

        if (currentActiveTab === TAB_INDICES.ATTENDANCE) {
            getAttendanceTracking();
        } else if (currentActiveTab === TAB_INDICES.REALTIME) {
            getRealtimeTracking();
        } else if (currentActiveTab === TAB_INDICES.HOSPITAL_VISIT) {
            getHospitalVisitTracking();
        }
    }, [getAttendanceTracking, getRealtimeTracking, getHospitalVisitTracking]);

    const handleReset = useCallback(() => {
        setFilterObj({
            fromDate: moment().toDate(),
            toDate: moment().toDate(),
            userCode: null,
        });
        setCurrentPage(1);
        setRealtimeCurrentPage(1);
        setHospitalVisitCurrentPage(1);
        setExpandedRows({});

        const currentActiveTab = activeTabRef.current;

        if (currentActiveTab === TAB_INDICES.ATTENDANCE) {
            getAttendanceTracking();
        } else if (currentActiveTab === TAB_INDICES.REALTIME) {
            getRealtimeTracking();
        } else if (currentActiveTab === TAB_INDICES.HOSPITAL_VISIT) {
            getHospitalVisitTracking();
        }
    }, [getAttendanceTracking, getRealtimeTracking, getHospitalVisitTracking]);

    const handleAttendancePageChange = useCallback((event, value) => {
        setCurrentPage(value);
        // Use setTimeout to ensure state is updated before API call
        setTimeout(() => {
            getAttendanceTracking();
        }, 0);
    }, [getAttendanceTracking]);

    const handleRealtimePageChange = useCallback((event, value) => {
        setRealtimeCurrentPage(value);
        setTimeout(() => {
            getRealtimeTracking();
        }, 0);
    }, [getRealtimeTracking]);

    const handleHospitalVisitPageChange = useCallback((event, value) => {
        setHospitalVisitCurrentPage(value);
        setTimeout(() => {
            getHospitalVisitTracking();
        }, 0);
    }, [getHospitalVisitTracking]);

    const handleChangeTab = useCallback((event, newValue) => {
        setActiveTab(newValue);
        setCurrentPage(1);
        setRealtimeCurrentPage(1);
        setHospitalVisitCurrentPage(1);
        setExpandedRows({});

        setTimeout(() => {
            if (newValue === TAB_INDICES.ATTENDANCE) {
                getAttendanceTracking();
            } else if (newValue === TAB_INDICES.REALTIME) {
                getRealtimeTracking();
            } else if (newValue === TAB_INDICES.HOSPITAL_VISIT) {
                getHospitalVisitTracking();
            }
        }, 0);
    }, [getAttendanceTracking, getRealtimeTracking, getHospitalVisitTracking]);

    const formatDateTime = useCallback((value) => {
        if (!value || value === "null" || value === "undefined" || value === null || value === undefined) {
            return "-";
        }

        const stringValue = String(value).trim();
        if (!stringValue) return "-";

        try {
            if (stringValue.includes('-') || stringValue.includes('/') || stringValue.includes(':')) {
                const date = new Date(stringValue);
                if (!Number.isNaN(date.getTime())) {
                    if (stringValue.includes(':')) {
                        return moment(date).format(DATE_FORMAT_WITH_TIME);
                    }
                    return moment(date).format(DATE_FORMAT);
                }
            }
            return stringValue;
        } catch {
            return stringValue;
        }
    }, []);

    const toggleRowExpansion = useCallback((rowKey) => {
        setExpandedRows(prev => ({
            ...prev,
            [rowKey]: !prev[rowKey]
        }));
    }, []);

    const isDataAvailable = useMemo(() => {
        if (activeTab === TAB_INDICES.ATTENDANCE) {
            return attendanceList.length > 0;
        } else if (activeTab === TAB_INDICES.REALTIME) {
            return realtimeList.length > 0;
        } else if (activeTab === TAB_INDICES.HOSPITAL_VISIT) {
            return hospitalVisitList.length > 0;
        }
        return false;
    }, [activeTab, attendanceList, realtimeList, hospitalVisitList]);

    const isExportDisabled = useMemo(() => {
        return exporting || !isDataAvailable;
    }, [exporting, isDataAvailable]);

    const isLoading = useMemo(() => {
        return loading || realtimeLoading || hospitalVisitLoading;
    }, [loading, realtimeLoading, hospitalVisitLoading]);

    const currentTableColumns = useMemo(() => {
        if (activeTab === TAB_INDICES.ATTENDANCE) return attendanceColumns;
        if (activeTab === TAB_INDICES.REALTIME) return realtimeColumns;
        return hospitalVisitColumns;
    }, [activeTab, attendanceColumns, realtimeColumns, hospitalVisitColumns]);

    const currentDataList = useMemo(() => {
        if (activeTab === TAB_INDICES.ATTENDANCE) return attendanceList;
        if (activeTab === TAB_INDICES.REALTIME) return realtimeList;
        return hospitalVisitList;
    }, [activeTab, attendanceList, realtimeList, hospitalVisitList]);

    const currentPageNumber = useMemo(() => {
        if (activeTab === TAB_INDICES.ATTENDANCE) return currentPage;
        if (activeTab === TAB_INDICES.REALTIME) return realtimeCurrentPage;
        return hospitalVisitCurrentPage;
    }, [activeTab, currentPage, realtimeCurrentPage, hospitalVisitCurrentPage]);

    const currentTotalRecords = useMemo(() => {
        if (activeTab === TAB_INDICES.ATTENDANCE) return attendanceTotalRecords;
        if (activeTab === TAB_INDICES.REALTIME) return realtimeTotalRecords;
        return hospitalVisitTotalRecords;
    }, [activeTab, attendanceTotalRecords, realtimeTotalRecords, hospitalVisitTotalRecords]);

    const handlePageChange = useCallback((event, value) => {
        if (activeTab === TAB_INDICES.ATTENDANCE) {
            handleAttendancePageChange(event, value);
        } else if (activeTab === TAB_INDICES.REALTIME) {
            handleRealtimePageChange(event, value);
        } else if (activeTab === TAB_INDICES.HOSPITAL_VISIT) {
            handleHospitalVisitPageChange(event, value);
        }
    }, [activeTab, handleAttendancePageChange, handleRealtimePageChange, handleHospitalVisitPageChange]);

    useEffect(() => {
        getConciergeUsers();
        getAttendanceTracking();
    }, []);

    return (
        <OurLayouts>
            <Backdrop
                sx={{ color: "#FFFFFF", zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={isLoading}
            >
                <CircularProgress size={80} color="inherit" />
            </Backdrop>

            <div className="flex flex-col w-full mt-[4rem] overflow-auto">
                <div className="flex justify-between mt-[3rem]">
                    <div>
                        <h1 className="text-2xl text-primary font-semibold">
                            Agent Report
                        </h1>
                    </div>
                    <div className="flex border h-10 rounded-md pt-1 pb-1 mr-12 justify-items-center">
                        <div className="flex mx-1">
                            <Button
                                className="flex gap-1"
                                onClick={exportToExcel}
                                disabled={isExportDisabled}
                                sx={{ textTransform: 'none' }}
                            >
                                <TiExport
                                    className="text-2xl"
                                    style={{ color: exporting ? "green" : "#004172" }}
                                />
                                <h6 style={{ color: exporting ? "green" : "#004172" }}>
                                    {exporting ? "Exporting..." : "Export Excel"}
                                </h6>
                            </Button>
                            {exporting && (
                                <ProgressSpinner
                                    style={{ width: "20px", height: "20px" }}
                                    strokeWidth="8"
                                    fill="var(--surface-ground)"
                                    animationDuration=".5s"
                                />
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex items-center mt-1 gap-4 mr-10 p-4 bg-gray-50 rounded-lg">
                    <div className="flex flex-col">
                        <label htmlFor="fromDate" className="text-sm font-medium text-gray-700 mb-1">From Date</label>
                        <Calendar
                            id="fromDate"
                            className="border border-gray-300 rounded-md p-2"
                            placeholder="Select From Date"
                            value={filterObj.fromDate}
                            onChange={(e) => handleDateChange(e, "fromDate")}
                            dateFormat="dd/mm/yy"
                            style={{ height: "40px", width: "180px" }}
                            maxDate={filterObj.toDate || new Date()}
                            showIcon
                        />
                    </div>

                    <div className="flex flex-col">
                        <label htmlFor="toDate" className="text-sm font-medium text-gray-700 mb-1">To Date</label>
                        <Calendar
                            id="toDate"
                            className="border border-gray-300 rounded-md p-2"
                            placeholder="Select To Date"
                            value={filterObj.toDate}
                            onChange={(e) => handleDateChange(e, "toDate")}
                            dateFormat="dd/mm/yy"
                            style={{ height: "40px", width: "180px" }}
                            minDate={filterObj.fromDate}
                            maxDate={new Date()}
                            showIcon
                        />
                    </div>

                    <div className="flex flex-col">
                        <label htmlFor="conciergeUser" className="text-sm font-medium text-gray-700 mb-1">Concierge User</label>
                        <Dropdown
                            id="conciergeUser"
                            value={filterObj.userCode}
                            options={conciergeUsers}
                            optionLabel="name"
                            placeholder="Select User"
                            className="border border-gray-300 rounded-md"
                            style={{ height: "40px", width: "220px" }}
                            onChange={(e) => setFilterObj(prev => ({ ...prev, userCode: e.value }))}
                            filter
                            showClear
                        />
                    </div>

                    <button
                        id="searchButton"
                        className="w-32 h-9 bg-[#004172] text-white mt-4 rounded-full"
                        onClick={handleSearch}
                        type="button"
                        aria-label="Search"
                    >
                        Search
                    </button>
                    <button
                        id="refreshButton"
                        className="w-32 h-9 bg-white text-[#004172] rounded-full mt-4 border border-[#004172]"
                        onClick={handleReset}
                        type="button"
                        aria-label="Refresh"
                    >
                        Refresh
                    </button>
                </div>

                <div
                    className="w-[99%] mt-4 rounded-[5px]"
                    style={{ boxShadow: "0px 8px 25px 0px rgba(0, 0, 0, 0.06)" }}
                >
                    <Tabs
                        value={activeTab}
                        onChange={handleChangeTab}
                        sx={{
                            '& .MuiTab-root': {
                                fontWeight: 'bold',
                                fontSize: '14px',
                                textTransform: 'none',
                            }
                        }}
                    >
                        <Tab label="Attendance Summary" />
                        <Tab label="Realtime Report" />
                        <Tab label="Hospital Visit Tracking" />
                    </Tabs>

                    {activeTab !== TAB_INDICES.HOSPITAL_VISIT && (
                        <>
                            <TableContainer
                                component={Paper}
                                sx={{
                                    width: "100%",
                                    overflowX: "auto",
                                    '& .MuiTableCell-root': {
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }
                                }}
                            >
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                            {currentTableColumns.map((column) => (
                                                <TableCell
                                                    key={column.field}
                                                    sx={{
                                                        fontSize: "14px",
                                                        fontWeight: 600,
                                                        minWidth: column.width,
                                                        maxWidth: column.width,
                                                        padding: "12px 16px"
                                                    }}
                                                >
                                                    {column.header}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {currentDataList.length > 0 ? (
                                            currentDataList.map((row, index) => {
                                                const rowKey = row.userCode || row.enquiryId || `${activeTab}_${index}`;
                                                return (
                                                    <TableRow
                                                        key={rowKey}
                                                        sx={{
                                                            '&:hover': { backgroundColor: '#f9f9f9' },
                                                            '&:nth-of-type(odd)': { backgroundColor: '#fafafa' }
                                                        }}
                                                    >
                                                        {currentTableColumns.map((column) => {
                                                            const displayValue = formatDateTime(row[column.field]);
                                                            return (
                                                                <TableCell
                                                                    key={`${rowKey}-${column.field}`}
                                                                    sx={{
                                                                        fontSize: "13px",
                                                                        fontWeight: 400,
                                                                        minWidth: column.width,
                                                                        maxWidth: column.width,
                                                                        padding: "10px 16px",
                                                                        borderBottom: "1px solid #e0e0e0"
                                                                    }}
                                                                    title={displayValue}
                                                                >
                                                                    <div className="truncate">
                                                                        {displayValue}
                                                                    </div>
                                                                </TableCell>
                                                            );
                                                        })}
                                                    </TableRow>
                                                );
                                            })
                                        ) : (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={currentTableColumns.length}
                                                    align="center"
                                                    sx={{
                                                        fontSize: "14px",
                                                        fontWeight: 500,
                                                        padding: "40px 16px",
                                                        color: "#666"
                                                    }}
                                                >
                                                    {isLoading ? "Loading..." : "No data available"}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {currentTotalRecords > ITEMS_PER_PAGE && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        marginTop: "2rem",
                                        paddingBottom: "1rem",
                                    }}
                                >
                                    <Pagination
                                        count={Math.ceil(currentTotalRecords / ITEMS_PER_PAGE)}
                                        page={currentPageNumber}
                                        color="primary"
                                        onChange={handlePageChange}
                                        showFirstButton
                                        showLastButton
                                    />
                                </Box>
                            )}
                        </>
                    )}

                    {activeTab === TAB_INDICES.HOSPITAL_VISIT && (
                        <>
                            <Box sx={{ p: 3 }}>
                                {hospitalVisitList.length > 0 ? (
                                    hospitalVisitList.map((batch) => {
                                        const isExpanded = expandedRows[batch.key];
                                        const hasVisits = batch.attendanceList && batch.attendanceList.length > 0;

                                        return (
                                            <Box
                                                key={batch.key}
                                                sx={{
                                                    mb: 3,
                                                    border: '1px solid #e0e0e0',
                                                    borderRadius: 2,
                                                    overflow: 'hidden',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        p: 2,
                                                        backgroundColor: '#f8f9fa',
                                                        cursor: 'pointer',
                                                        borderBottom: isExpanded ? '1px solid #e0e0e0' : 'none',
                                                        '&:hover': {
                                                            backgroundColor: '#f0f7ff'
                                                        }
                                                    }}
                                                    onClick={() => toggleRowExpansion(batch.key)}
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            toggleRowExpansion(batch.key);
                                                        }
                                                    }}
                                                    aria-expanded={isExpanded}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <MdAccessTime className="text-gray-500" />
                                                                <Typography variant="body1" fontWeight="600">
                                                                    Date: {formatDateTime(batch.createdDate)}
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <FaUserMd className="text-blue-500" />
                                                                <Typography variant="body1" fontWeight="500">
                                                                    User Code: {batch.userId || "-"}
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <FaUserMd className="text-green-500" />
                                                                <Typography variant="body1" fontWeight="500">
                                                                    User Name: {batch.userName || "-"}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <Chip
                                                            label={`${batch.totalVisits || 0} Visits`}
                                                            size="small"
                                                            color="primary"
                                                            variant="outlined"
                                                            sx={{ fontWeight: 600 }}
                                                        />
                                                        <Typography variant="caption" color="text.secondary">
                                                            {isExpanded ? "Click to collapse" : "Click to expand"}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                    <Box sx={{ mb: 3 }}>
                                                        <Box sx={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            mb: 1,
                                                            p: 2,
                                                            borderRadius: 1
                                                        }}>
                                                            <Typography variant="h6" fontWeight="600" color="#0d47a1">
                                                                Hospital Visit Details
                                                            </Typography>
                                                        </Box>

                                                        {hasVisits ? (
                                                            <TableContainer
                                                                component={Paper}
                                                                variant="outlined"
                                                                sx={{ borderRadius: 2 }}
                                                            >
                                                                <Table size="small">
                                                                    <TableHead>
                                                                        <TableRow sx={{ backgroundColor: '#f0f7ff' }}>
                                                                            {visitDetailColumns.map((column) => (
                                                                                <TableCell
                                                                                    key={column.field}
                                                                                    sx={{
                                                                                        fontSize: "13px",
                                                                                        fontWeight: 600,
                                                                                        padding: "12px 16px",
                                                                                        color: "#0d47a1"
                                                                                    }}
                                                                                >
                                                                                    {column.header}
                                                                                </TableCell>
                                                                            ))}
                                                                        </TableRow>
                                                                    </TableHead>
                                                                    <TableBody>
                                                                        {batch.attendanceList.map((visit, visitIndex) => (
                                                                            <TableRow
                                                                                key={`${batch.key}-visit-${visitIndex}`}
                                                                                sx={{
                                                                                    '&:hover': { backgroundColor: '#f9fbfd' },
                                                                                    '&:nth-of-type(odd)': { backgroundColor: '#fafcff' }
                                                                                }}
                                                                            >
                                                                                <TableCell sx={{
                                                                                    fontSize: "13px",
                                                                                    padding: "10px 16px",
                                                                                    fontWeight: 500
                                                                                }}>
                                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                        <MdLocationOn className="text-gray-500" />
                                                                                        {visit.hospitalCode || "-"}
                                                                                    </Box>
                                                                                </TableCell>
                                                                                <TableCell sx={{ fontSize: "13px", padding: "10px 16px" }}>
                                                                                    {visit.hospitalName || "-"}
                                                                                </TableCell>
                                                                                <TableCell sx={{ fontSize: "13px", padding: "10px 16px" }}>
                                                                                    <Box sx={{
                                                                                        borderRadius: 1,
                                                                                        fontWeight: 500,
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        gap: 1
                                                                                    }}>
                                                                                        <FaClock className="text-gray-500" />
                                                                                        {formatDateTime(visit.checkIn)}
                                                                                    </Box>
                                                                                </TableCell>
                                                                                <TableCell sx={{ fontSize: "13px", padding: "10px 16px" }}>
                                                                                    <Box sx={{
                                                                                        borderRadius: 1,
                                                                                        fontWeight: 500,
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        gap: 1
                                                                                    }}>
                                                                                        <FaClock className="text-gray-500" />
                                                                                        {formatDateTime(visit.checkOut)}
                                                                                    </Box>
                                                                                </TableCell>
                                                                                <TableCell sx={{ fontSize: "13px", padding: "10px 16px" }}>
                                                                                    <Box sx={{
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        gap: 1
                                                                                    }}>
                                                                                        <MdAccessTime className="text-gray-500" />
                                                                                        {visit.hoursSpent || "-"}
                                                                                    </Box>
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        ))}
                                                                    </TableBody>
                                                                </Table>
                                                            </TableContainer>
                                                        ) : (
                                                            <Paper
                                                                elevation={0}
                                                                sx={{
                                                                    p: 4,
                                                                    textAlign: 'center',
                                                                    bgcolor: '#fafafa',
                                                                    borderRadius: 2
                                                                }}
                                                            >
                                                                <Typography variant="body1" color="text.secondary">
                                                                    No hospital visit records found for this user
                                                                </Typography>
                                                            </Paper>
                                                        )}
                                                    </Box>
                                                </Collapse>
                                            </Box>
                                        );
                                    })
                                ) : (
                                    <Box sx={{
                                        textAlign: 'center',
                                        py: 6,
                                        backgroundColor: '#f9f9f9',
                                        borderRadius: 2
                                    }}>
                                        <Typography variant="body1" color="text.secondary">
                                            {hospitalVisitLoading ? "Loading..." : "No data available"}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>

                            {hospitalVisitTotalRecords > ITEMS_PER_PAGE && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        marginTop: "2rem",
                                        paddingBottom: "1rem",
                                    }}
                                >
                                    <Pagination
                                        count={Math.ceil(hospitalVisitTotalRecords / ITEMS_PER_PAGE)}
                                        page={hospitalVisitCurrentPage}
                                        color="primary"
                                        onChange={handleHospitalVisitPageChange}
                                        showFirstButton
                                        showLastButton
                                    />
                                </Box>
                            )}
                        </>
                    )}
                </div>
            </div>
        </OurLayouts>
    );
};

export default AgentReport;